import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import userSelectors from '@redux/user/selectors';

import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import { useGetProductFullView } from 'hooks';
import useCheckAssignProductToSupplier from 'pages/qa-spec/hooks/useCheckAssignProductToSupplier';

import { checkIsDollarProduct } from 'pages/qa-spec/views/qa-spec-detail/utils';

import * as qasSpecServices from 'services/qas-require-field';

export const requiredFieldsMap = {
  'eCommerce.smartLabelTemplate': 'qaSpecification.smartLabelTemplate',
  //* product spec tab
  targetMarket: 'qaSpecification.qaSpecProductSpecification.targetMarket',
  'placeOfItemActivity.placeOfProductActivity.countryOfOrigin.countryCode':
    'qaSpecification.qaSpecProductSpecification.countryOfOrigin',
  'tdcProperties.codeDateExample':
    'qaSpecification.qaSpecProductSpecification.codeDateExample',
  'tdcProperties.unit_MinimumShelfLife':
    'qaSpecification.qaSpecProductSpecification.minimumShelfLife',
  'tdcProperties.case_TemperatureRangeHigh':
    'qaSpecification.qaSpecProductSpecification.caseTemperatureRangeHigh',
  'tdcProperties.case_TemperatureRangeLow':
    'qaSpecification.qaSpecProductSpecification.caseTemperatureRangeLow',
  'tdcProperties.case_ShippingTemperatureRangeHigh':
    'qaSpecification.qaSpecProductSpecification.caseShippingTemperatureHigh',
  'tdcProperties.case_ShippingTemperatureRangeLow':
    'qaSpecification.qaSpecProductSpecification.caseShippingTemperatureLow',

  //* Dollar general additional fields
  //* product spec tab
  'tdcProperties.codeDateStamp':
    'qaSpecification.qaSpecProductSpecification.codeDateStamp',
  'tdcProperties.codeDateType':
    'qaSpecification.qaSpecProductSpecification.codeDateType',
  'tdcProperties.codeDateFormula':
    'qaSpecification.qaSpecProductSpecification.codeDateFormula',

  'tdcProperties.unit_MaximumShelfLife':
    'qaSpecification.qaSpecProductSpecification.maximumShelfLife',
  'tdcProperties.shelfLifeAfterOpening':
    'qaSpecification.qaSpecProductSpecification.shelfLifeAfterOpening',

  'tdcProperties.gMOStatement':
    'qaSpecification.qaSpecProductSpecification.gmoStatement',
  'tdcProperties.nonGmoGmoStatement':
    'qaSpecification.qaSpecProductSpecification.nonGmoStatement',
  'tdcProperties.productContainsBioengineeredIngredients':
    'qaSpecification.qaSpecProductSpecification.productContainsBioengineeredIngredients',
  'tdcProperties.bioengineeredStatement':
    'qaSpecification.qaSpecProductSpecification.bioengineeredStatement',
  'tdcProperties.rbghBstStatement':
    'qaSpecification.qaSpecProductSpecification.rbghBstStatement',
  'tdcProperties.californiaProp65WarningStatement':
    'qaSpecification.qaSpecProductSpecification.californiaProp65WarningStatement',
  'tdcProperties.fairTradeStatement':
    'qaSpecification.qaSpecProductSpecification.fairTradeStatement',
  'tdcProperties.uSDAStatement':
    'qaSpecification.qaSpecProductSpecification.usdaStatement',
  'tdcProperties.productContainPfas':
    'qaSpecification.qaSpecProductSpecification.productContainPfas',
  'tdcProperties.productPackagingContainPfas':
    'qaSpecification.qaSpecProductSpecification.productPackagingContainPfas',
  'tdcProperties.pfasTested':
    'qaSpecification.qaSpecProductSpecification.pfasTested',
  'tdcProperties.pfasStatement':
    'qaSpecification.qaSpecProductSpecification.pfasStatement',
  'qaSpecification.safeHandlingInstructions':
    'qaSpecification.qaSpecProductSpecification.safeHandlingInstructions',

  // ingredient
  'dProperties.ingredientsProductClaims':
    'qaSpecification.qaSpecIngredients.ingredientClaims',

  // allergens
  'dProperties.allergenStatement':
    'qaSpecification.qaSpecAllergens.allergenStatement',

  // contacts
  'qaSpecification.qaSpecContacts.productionFacilityNo1.name':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityName',

  'qaSpecification.qaSpecContacts.productionFacilityNo1.addressLine1':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityAddressLine1',

  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManager':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerName',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerPhone',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerEmail',

  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaContact':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaName',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaEmail',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaPhone',

  //* End

  //* labeling tab
  'consumerInstructions.consumerInstructions.consumerUsageInstructions.value':
    'qaSpecification.labeling.usage',
  'consumerInstructions.consumerInstructions.consumerStorageInstructions.value':
    'qaSpecification.labeling.storageInstructions',
  'tradeItemHandling.tradeItemHandlingInformation.handlingInstructionsDescription.value':
    'qaSpecification.labeling.handlingInstructions',
  'qaSpecification.qaSpecPackaging.thawingInstructions':
    'qaSpecification.labeling.thawingInstructions',
  'packagingMarking.packagingMarking.consumerWarningInformation.consumerWarningDescription.value':
    'qaSpecification.labeling.warningStatement',
  'qaSpecification.qaSpecPackaging.frontOfPackIcons':
    'qaSpecification.labeling.frontOfPackIcons',
  'qaSpecification.qaSpecPackaging.recyclable':
    'qaSpecification.labeling.recyclable',
  'sustainability.sustainabilityInformation.totalRecyclableContentPercentage':
    'qaSpecification.labeling.totalRecyclableContentPercentage',
  'sustainability.sustainabilityInformation.postConsumerRecycledContentPercentage':
    'qaSpecification.labeling.postConsumerRecycledContentPercentage',
  'packagingInformation.packaging.packagingTypeCode':
    'qaSpecification.labeling.packagingType',
  'packagingInformation.packaging.packagingMaterial.packagingMaterialTypeCode':
    'qaSpecification.labeling.packagingMaterial',
  'tdcProperties.qRCode': 'qaSpecification.labeling.qrCode',
  'qaSpecification.qaSpecPackaging.bilingual':
    'qaSpecification.labeling.bilingual',
  'eCommerce.rivirMarketingDescription': 'qaSpecification.labeling.romanceCopy',
  'foodAndBeveragePreparationServing.preparationServing.preparationInstructions.value':
    'qaSpecification.labeling.cookingInstructions',
  'foodAndBeveragePreparationServing.preparationServing.recipe.value':
    'qaSpecification.labeling.recipe',
  'qaSpecification.qaSpecPackaging.otherCertifications':
    'qaSpecification.labeling.otherCertifications',
  'tdcProperties.hasFoodGradePlasticsCertifier':
    'qaSpecification.labeling.hasFoodGradePlasticsCertifier',
  'tdcProperties.recycledMaterial': 'qaSpecification.labeling.recycledMaterial',
  'tdcProperties.soyInk': 'qaSpecification.labeling.soyInk',
  'tdcProperties.vegetableBasedInk':
    'qaSpecification.labeling.vegetableBasedInk',
  productName: 'qaSpecification.labeling.productName',
  upc12: 'qaSpecification.labeling.upc12',
  primarySize: 'qaSpecification.labeling.size',
  primarySizeUOM: 'qaSpecification.labeling.uom',
  'qaSpecification.qaSpecPackaging.claim1': 'qaSpecification.labeling.claim1',
  'dProperties.bpSKULabelling.leftLanguage.tertiaryDescriptor':
    'qaSpecification.labeling.tertiaryDescriptor',
  'dProperties.bpSKULabelling.leftLanguage.frontPanelSecondaryDescrip':
    'qaSpecification.labeling.secondaryDescriptor',
  'qaSpecification.qaSpecPackaging.allergenStatement':
    'qaSpecification.labeling.allergenStatement',
  'qaSpecification.qaSpecPackaging.ingredientStatement':
    'qaSpecification.labeling.ingredientStatement',
  'qaSpecification.qaSpecPackaging.productContainsBioengineeredIngredients':
    'qaSpecification.labeling.productContainsBioengineeredIngredients',
  'qaSpecification.qaSpecPackaging.bioengineeredIngredientsStatement':
    'qaSpecification.labeling.bioengineeredStatement',
  //* ingredient tab
  'foodAndBeverageIngredient.ingredientStatement.value':
    'qaSpecification.qaSpecIngredients.ingredientStatement',
};

const dollarGeneralRequiredFields = {
  'qaSpecification.qaSpecProductSpecification': [
    'productName',
    'codeDateStamp',
    'codeDateType',
    'codeDateFormula',
    'gmoStatement',
    'nonGmoStatement',
    'bioengineeredStatement',
    'shelfLifeAfterOpening',
    'maximumShelfLife',
    'productContainsBioengineeredIngredients',
    'rbghBstStatement',
    'californiaProp65WarningStatement',
    'fairTradeStatement',
    'usdaStatement',
    'productContainPfas',
    'productPackagingContainPfas',
    'pfasTested',
    'pfasStatement',
    'safeHandlingInstructions',
    'liveActiveCultures',
    'usdaGrade',
    'usdaShieldId',
    'eggsGrading',
  ],
  'qaSpecification.qaSpecIngredients': [
    'ingredientClaims',
    'function',
    'origin',
    'ingredientSupplier',
    'religiousClaim',
    'bioengineered',
    'ionized',
    'allergens',
    'percentAddedOnProduction',
  ],

  'qaSpecification.qaSpecAllergens': ['allergenStatement'],

  'qaSpecification.qaSpecContacts': [
    'primaryBusinessContact.addressLine1',
    'primaryBusinessContact.country',
    'primaryQcContact.addressLine1',
    'primaryQcContact.country',
    '_24HourContact.addressLine1',
    '_24HourContact.country',
    'productionFacilityNo1.facilityName',
    'productionFacilityNo1.facilityAddressLine1',
    'productionFacilityNo1.country',
    'productionFacilityNo1.faxNumber',
    'productionFacilityNo1.facilityPlantManagerName',
    'productionFacilityNo1.facilityPlantManagerPhone',
    'productionFacilityNo1.facilityPlantManagerEmail',
    'productionFacilityNo1.facilityQaEmail',
    'productionFacilityNo1.facilityQaName',
    'productionFacilityNo1.facilityQaPhone',
  ],
  'qaSpecification.labeling': [
    'productContainsBioengineeredIngredients',
    'bioengineeredStatement',
    'allergenStatement',
    'ingredientStatement',
    'secondaryDescriptor',
    'tertiaryDescriptor',
    'claim1',
    'upc12',
    'productName',
    'size',
    'uom',
    'usage',
    'storageInstructions',
    'handlingInstructions',
    'thawingInstructions',
    'warningStatement',
    'frontOfPackIcons',
    'recyclable',
    'totalRecyclableContentPercentage',
    'postConsumerRecycledContentPercentage',
    'packagingType',
    'packagingMaterial',
    'qrCode',
    'bilingual',
    'romanceCopy',
    'cookingInstructions',
    'recipe',
    'otherCertifications',
    'hasFoodGradePlasticsCertifier',
    'recycledMaterial',
    'soyInk',
    'vegetableBasedInk',
  ],

  'qaSpecification.qaSpecManufacturingProcesses': [],
};

const addDollarGeneralRequiredFields = () => {
  return Object.keys(dollarGeneralRequiredFields).reduce(
    (accumulator, section) => {
      const sectionFields = dollarGeneralRequiredFields[section].map(
        (fieldName) => {
          return `${section}.${fieldName}`;
        }
      );

      return [...accumulator, ...sectionFields];
    },
    []
  );
};

export const getGetQaSpecRequiredFieldsKeys = (params, userId) => {
  return ['get-qa-spec-required-fields', params, userId];
};

export const useGetQaSpecRequiredFields = (props) => {
  const { params, fieldPath, ...options } = props;

  const userInfo = useSelector(userSelectors.makeSelectUserInfo());

  const isTypeFood = params?.productType?.toLowerCase() === 'food';

  const isDollarProduct = checkIsDollarProduct(params?.ownerId);

  const getGetQaSpecRequiredFields = async ({ queryKey }) => {
    const params = queryKey[1];

    const response = await qasSpecServices.getQaSpecRequiredFieldForProduct(
      params
    );

    if (!response.isSuccess || !response?.data?.requiredFields) return;

    //* map data
    const requiredFields = response.data.requiredFields
      .filter((field) => {
        //* filter out product contain field when product does not belong to dollar general

        //* if product  belongs to dollar general, accept all fields
        if (isDollarProduct) return true;

        //* if not, accept fields that is not product contain fields
        const isProductContainField = field.includes(
          'qaSpecification.qaSpecProductContains'
        );

        return !isProductContainField;
      })
      .map((field) => {
        //* some fields  in Product Spec tab have difference fieldName with required fields, format them to standard field full path for logic flow
        const formattedFields = requiredFieldsMap[field];

        let finalField = formattedFields ? formattedFields : field;

        return finalField;
      });

    //* make dollar general product required
    const additionalDGRequiredFields =
      isDollarProduct && isTypeFood
        ? addDollarGeneralRequiredFields(requiredFields)
        : [];

    return [...requiredFields, ...additionalDGRequiredFields];
  };

  return useQuery({
    queryKey: getGetQaSpecRequiredFieldsKeys(params, { userId: userInfo?.id }),
    queryFn: getGetQaSpecRequiredFields,
    ...options,
  });
};

export const useGetQaSpecRequiredFieldsInQaSpecFullView = (props) => {
  const { productId } = props;

  const { productFull } = useGetProductFullView({
    productId,
  });

  const { qaSpecDataHeader, defaultType } = useGetQaSpecDataHeader({
    id: productId,
    isEnabled: !!productId,
  });

  const productType = qaSpecDataHeader?.type || defaultType;

  const { checkAssignProductToSupplier } = useCheckAssignProductToSupplier();
  const isProductAssignedByRetailer = checkAssignProductToSupplier();

  return useGetQaSpecRequiredFields({
    params: {
      ownerId: productFull?.ownerId,
      productType,
    },
    enabled:
      !!isProductAssignedByRetailer &&
      !!productFull &&
      !!productType &&
      !!productFull?.ownerId,
  });
};
